import { useState } from 'react';
import { useMediaQuery } from 'react-responsive';

import { CompanyNotificationType, getUserCompanyNotifications } from '@/services/ws-comments/src/main/resources/rest-api';
import { countCreatives, CreativeStatus } from '@/services/ws-creatives/src/main/resources/rest-api';
import { getPersonalComments } from '@/services/ws-orders/src/main/resources/rest-api';
import { createContexts } from '@/js/libs/create-contexts';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { usePollingInterval } from '@/js/libs/hooks/usePollingInterval';
import { useSubscribe } from '@/js/libs/observable';
import { Media } from '@/js/libs/ui';
import { useIdentityRoles } from '@/js/libs/use-auth';
import { NotActive, useMappedRequest, useRequest } from '@/js/libs/use-request';

import useLocalStorage, { StorageKey } from 'src/hooks/useLocalStorage.ts';
import useSessionStorage, { StorageKey as SessionStorageKey, StorageScope as SessionStorageScope } from 'src/hooks/useSessionStorage.ts';

const MAX_VISIBLE_CRITICAL_NOTIFICATIONS = 3;

export const { useNavigationContext } = createContexts({
  Navigation() {
    const { saveBoolean, loadBoolean } = useLocalStorage();
    const isMobile = useMediaQuery({ query: Media.xsMax });
    const { ADMIN, AGENCY, USER, MANAGE_DIGITAL_PRODUCTS, OPS_PUSH } = useIdentityRoles();
    const [isCollapsedNav, setIsCollapsedNav] = useState(isMobile ? true : loadBoolean(StorageKey.PUZZLE_NAV_COLLAPSED));

    const unreadCommentsCountRequest = useRequest({
      apiMethod: getPersonalComments,
      params: ADMIN || AGENCY ? { showRead: false, page: 0, limit: 1 } : NotActive,
      silent: true,
    });
    const unreadCommentsCountMappedRequest = useMappedRequest(unreadCommentsCountRequest, (data) => data.total);

    const unreadSystemNotificationsCountRequest = useRequest({
      apiMethod: getUserCompanyNotifications,
      params: { showRead: false, page: 0, limit: 1 },
      silent: true,
    });
    const unreadSystemNotificationsCountMappedRequest = useMappedRequest(unreadSystemNotificationsCountRequest, (data) => data.total);

    const criticalNotificationsRequest = useRequest({
      apiMethod: getUserCompanyNotifications,
      params: { type: CompanyNotificationType.CRITICAL, showRead: false, page: 0, sort: 'startDate', order: 'desc' },
      silent: true,
    });
    const { saveJSON: saveDismissedNotificationIds, loadJSON: loadDismissedNotificationIds } = useSessionStorage(
      SessionStorageScope.NOTIFICATIONS_CRITICAL_BANNER,
    );

    const [dismissedCriticalNotificationIds, setDismissedCriticalNotificationIds] = useState(
      () => loadDismissedNotificationIds<string[]>(SessionStorageKey.DISMISSED_NOTIFICATION_IDS) ?? [],
    );

    const criticalNotifications = useSubscribe(
      criticalNotificationsRequest.data$,
      (data) =>
        (data?.data.items ?? [])
          .filter((item) => !dismissedCriticalNotificationIds.includes(item.id))
          .slice(0, MAX_VISIBLE_CRITICAL_NOTIFICATIONS),
      [dismissedCriticalNotificationIds],
    );

    const dismissCriticalNotification = useCallbackRef((id: string) => {
      const dismissedIds = [...dismissedCriticalNotificationIds, id];
      setDismissedCriticalNotificationIds(dismissedIds);
      saveDismissedNotificationIds(SessionStorageKey.DISMISSED_NOTIFICATION_IDS, dismissedIds);
    });

    const countCreativesRequest = useRequest({
      apiMethod: countCreatives,
      params: !ADMIN && !(MANAGE_DIGITAL_PRODUCTS && OPS_PUSH && !USER) ? NotActive : { creativeStatus: CreativeStatus.PENDING_APPROVAL },
    });

    const articlesCount = useSubscribe(countCreativesRequest.data$, (response) => response?.data ?? 0);
    const pendingArticlesCount = useSubscribe(countCreativesRequest.pending$);

    const collapseMainNav = useCallbackRef(() => {
      saveBoolean(StorageKey.PUZZLE_NAV_COLLAPSED, !isCollapsedNav);
      setIsCollapsedNav(!isCollapsedNav);
    });

    usePollingInterval(
      () => {
        unreadSystemNotificationsCountRequest.retry();
        unreadCommentsCountRequest.retry();
        criticalNotificationsRequest.retry();
      },
      { intervalMs: 60_000 },
    );

    return {
      isCollapsedNav,
      collapseMainNav,
      unreadCommentsCountRequest: unreadCommentsCountMappedRequest,
      unreadSystemNotificationsCountRequest: unreadSystemNotificationsCountMappedRequest,
      reloadUnreadSystemNotificationsCount: unreadSystemNotificationsCountRequest.retry,
      reloadUnreadCommentsCount: unreadCommentsCountRequest.retry,
      articlesCount,
      pendingArticlesCount,
      reloadApprovalCounter: countCreativesRequest.retry,
      criticalNotifications,
      dismissCriticalNotification,
      reloadCriticalNotifications: criticalNotificationsRequest.retry,
    };
  },
});
